import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PencilSimple, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [perms, setPerms] = useState(["view", "create", "edit", "delete", "approve"]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: {} });

  const load = () => {
    api.get("/roles").then((r) => setRoles(r.data));
    api.get("/roles/modules").then((r) => { setModules(r.data.modules); setPerms(r.data.permissions); });
  };
  useEffect(() => { load(); }, []);
  const pg = usePaginated(roles);

  const openNew = () => {
    const emptyPerms = {}; modules.forEach(m => { emptyPerms[m] = { view: false, create: false, edit: false, delete: false, approve: false }; });
    setForm({ name: "", description: "", permissions: emptyPerms }); setEditing(null); setOpen(true);
  };
  const edit = (r) => {
    const merged = {}; modules.forEach(m => { merged[m] = { view: false, create: false, edit: false, delete: false, approve: false, ...(r.permissions?.[m] || {}) }; });
    setForm({ name: r.name, description: r.description || "", permissions: merged }); setEditing(r); setOpen(true);
  };
  const save = async () => {
    try {
      if (editing) await api.put(`/roles/${editing.id}`, form);
      else await api.post("/roles", form);
      toast.success("Role tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus role?")) return; try { await api.delete(`/roles/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };

  const setPerm = (mod, key, val) => setForm({ ...form, permissions: { ...form.permissions, [mod]: { ...form.permissions[mod], [key]: val } } });
  const toggleAll = (mod, val) => { const p = {}; perms.forEach(k => p[k] = val); setForm({ ...form, permissions: { ...form.permissions, [mod]: p } }); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl" data-testid="roles-page">
      <div className="flex justify-between items-baseline">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Role & Hak Akses</h1>
          <p className="text-sm text-slate-500 mt-1">Buat role kustom & pilih modul mana saja yang boleh diakses per role.</p>
        </div>
        <Button className="bg-amber-600 hover:bg-amber-700" onClick={openNew} data-testid="new-role-btn"><Plus size={16} className="mr-1" />Role Baru</Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md" data-testid="roles-table">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Deskripsi</TableHead><TableHead>Jenis</TableHead><TableHead>Ringkasan Akses</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {roles.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500 text-sm">Belum ada role.</TableCell></TableRow> : pg.paged.map((r) => {
              const grantedMods = Object.entries(r.permissions || {}).filter(([_, p]) => p?.view).map(([m]) => m);
              return (
                <TableRow key={r.id}>
                  <TableCell className="font-medium flex items-center gap-2"><ShieldCheck size={14} weight="duotone" className="text-amber-600" />{r.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.description || "—"}</TableCell>
                  <TableCell>{r.builtin ? <span className="text-xs px-2 py-0.5 rounded bg-slate-100">Bawaan</span> : <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Kustom</span>}</TableCell>
                  <TableCell className="text-xs text-slate-500 truncate max-w-[280px]">{grantedMods.length} modul: {grantedMods.slice(0, 5).join(", ")}{grantedMods.length > 5 ? "..." : ""}</TableCell>
                  <TableCell className="flex gap-1">
                    {!r.builtin && <>
                      <button onClick={() => edit(r)} className="text-slate-400 hover:text-slate-900"><PencilSimple size={14} /></button>
                      <button onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600"><Trash size={14} /></button>
                    </>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {pg.pager}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Buat"} Role Kustom</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama Role</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name" /></div>
            <div><Label>Deskripsi</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label>Matriks Akses per Modul</Label>
              <div className="bg-white border rounded-md overflow-x-auto mt-1">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="bg-slate-100"><tr><th className="text-left p-2">Modul</th><th className="p-2">View</th><th className="p-2">Create</th><th className="p-2">Edit</th><th className="p-2">Delete</th><th className="p-2" title="Approve SO/PO (khusus modul sales & purchases)">Approve</th><th className="p-2">Semua</th></tr></thead>
                  <tbody>
                    {modules.map(m => {
                      const isApprovable = m === "sales" || m === "purchases";
                      return (
                      <tr key={m} className="border-t">
                        <td className="p-2 capitalize font-medium">{m.replace(/_/g, ' ')}</td>
                        {perms.map(p => (
                          <td key={p} className="p-2 text-center">
                            {p === "approve" && !isApprovable ? (
                              <span className="text-stone-300 text-xs">—</span>
                            ) : (
                              <input type="checkbox" checked={form.permissions[m]?.[p] || false} onChange={(e) => setPerm(m, p, e.target.checked)} data-testid={`perm-${m}-${p}`} />
                            )}
                          </td>
                        ))}
                        <td className="p-2 text-center"><input type="checkbox" onChange={(e) => toggleAll(m, e.target.checked)} /></td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>
            <Button onClick={save} className="w-full bg-slate-900" data-testid="role-save-btn">Simpan Role</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
